import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useKitchenPrintersQuery,
  useKitchenReprintRoundsQuery,
  type KitchenPrinter,
  useReprintKitchenMutation,
  useReprintOnlineOrderKitchenMutation,
  useLazyPrintTotalStatusQuery,
} from '@/features/waiter/waiterApi';
import {
  printOutcomeDestination,
  reportPrintOutcome,
  KITCHEN_REPRINT_OUTCOME_MESSAGES,
} from '@/features/waiter/printOutcome';
import { useModalBackClose } from './useModalBackClose';
import type { ShowWaiterToast } from './waiterToast';

export type KitchenReprintSheetProps = {
  /** Waiter JWT — les deux appels de cette feuille le portent. */
  token: string;
  onClose: () => void;
  showToast: ShowWaiterToast;
} & (
  | { sessionUuid: string; orderUuid?: undefined }
  /**
   * Recette QA 2026-09-22 — une commande passée par le client sur le site n'a
   * pas de session : on vise la commande. Elle n'a qu'une tournée, donc taper
   * un poste imprime tout de suite, sans l'étape « quelle tournée ».
   */
  | { orderUuid: string; sessionUuid?: undefined }
);

/**
 * SC-1038 — « Réimprimer en cuisine », un poste à la fois.
 *
 * La cuisine dit « on n'a jamais eu le bon », mais c'est UN poste qui l'a
 * perdu : réimprimer tous les postes fait ressortir au froid un bon qu'il a
 * déjà exécuté, et le cuisinier doit deviner lequel est neuf. Le serveur vise
 * donc le poste depuis la table où il se trouve.
 *
 * Le geste se termine de deux façons selon la sortie du restaurant : en
 * destination « cloud » sale_api a déjà poussé le travail et le papier est
 * sorti (`dispatch_uuid` vide, rien à suivre) ; sinon un écran du comptoir ou
 * la caisse doit s'en saisir, et c'est la ligne d'attente qui dit ce que la
 * demande est devenue — même sondage que le « Imprimer » du pied de page.
 */
export default function KitchenReprintSheet(props: KitchenReprintSheetProps) {
  const { sessionUuid, orderUuid, token, onClose, showToast } = props;
  const { t } = useTranslation();
  const { data: printers, isFetching } = useKitchenPrintersQuery({ token }, { skip: !token });
  const [selectedPrinter, setSelectedPrinter] = useState<KitchenPrinter | null>(null);
  const {
    currentData: rounds,
    isFetching: roundsLoading,
    isError: roundsError,
    refetch: refetchRounds,
  } = useKitchenReprintRoundsQuery(
    { sessionUuid: sessionUuid ?? '', printerUuid: selectedPrinter?.uuid ?? '', token },
    { skip: !selectedPrinter || !sessionUuid, refetchOnMountOrArgChange: true },
  );
  const sending = useRef(false);
  const [reprintKitchen] = useReprintKitchenMutation();
  const [reprintOnlineOrderKitchen] = useReprintOnlineOrderKitchenMutation();
  const [fetchPrintTotalStatus] = useLazyPrintTotalStatusQuery();
  // Quel poste est parti : un seul à la fois, et le nom du poste reste lisible
  // pendant l'envoi — le serveur appuie depuis la salle, il doit voir QUE son
  // appui a été pris, pas seulement que tout est grisé.
  const [busyPrinterUuid, setBusyPrinterUuid] = useState<string | null>(null);

  useModalBackClose('kitchen-reprint-sheet', onClose);

  async function handlePrint(printer: KitchenPrinter | null, roundNumber?: number) {
    if (!printer || sending.current) return;
    sending.current = true;
    const printerUuid = printer.uuid;
    setBusyPrinterUuid(printerUuid);
    try {
      const queued =
        props.orderUuid !== undefined
          ? await reprintOnlineOrderKitchen({ orderUuid: props.orderUuid, printerUuid, token }).unwrap()
          : await reprintKitchen({
              sessionUuid: props.sessionUuid,
              printerUuid,
              roundNumber,
              token,
            }).unwrap();
      const destination = printOutcomeDestination(queued.destination);
      // La feuille a fait son travail : elle se referme sur le geste réussi,
      // le suivi continue en toast par-dessus l'écran Commandes.
      onClose();
      if (destination === 'cloud' || !destination || !queued.dispatchUuid) {
        showToast(t('waiter.ticket.commandes.reprintKitchen.done'));
        return;
      }
      showToast(t(KITCHEN_REPRINT_OUTCOME_MESSAGES[destination].sent));
      await reportPrintOutcome(
        queued.dispatchUuid,
        destination,
        {
          fetchStatus: async () => {
            const states = await Promise.all(
              queued.dispatchUuids.map((dispatchUuid) =>
                fetchPrintTotalStatus({ dispatchUuid, token }).unwrap(),
              ),
            );
            return (
              states.find((status) => status.state === 'failed') ??
              (states.every((status) => status.state === 'printed')
                ? { state: 'printed', detail: '' }
                : {
                    state: states.some((status) => status.state === 'waiting')
                      ? 'waiting'
                      : 'printing',
                    detail: '',
                  })
            );
          },
          announce: showToast,
          t,
        },
        KITCHEN_REPRINT_OUTCOME_MESSAGES[destination],
      );
    } catch (err) {
      const payload = err as { status?: number | string; data?: { code?: string } };
      // PRINT_EMPTY n'est pas une panne : ce poste n'imprime simplement aucun
      // plat de cette table. La feuille RESTE ouverte — le serveur s'est
      // trompé de poste, son geste suivant est d'en viser un autre.
      if (payload?.data?.code === 'PRINT_EMPTY' || payload?.status === 404) {
        showToast(t(`waiter.ticket.commandes.reprintKitchen.${orderUuid ? 'emptyOrder' : 'empty'}`));
      } else if (payload?.data?.code === 'PRINT_FAILED') {
        // L'imprimante cloud a refusé le papier : le dire, plutôt qu'une erreur
        // générique qui ferait recommencer le geste à l'aveugle.
        showToast(t('waiter.ticket.commandes.reprintKitchen.outcome.cloud.failed'));
      } else {
        showToast(t('waiter.ticket.errors.generic'));
      }
    } finally {
      sending.current = false;
      setBusyPrinterUuid(null);
    }
  }

  return (
    <div
      data-testid="kitchen-reprint-overlay"
      className="fixed inset-0 z-[1190] flex items-end justify-center"
      style={{ backgroundColor: 'var(--w-scrim)' }}
      onClick={onClose}
    >
      <div
        data-testid="kitchen-reprint-sheet"
        role="dialog"
        aria-modal="true"
        aria-label={t('waiter.ticket.commandes.reprintKitchen.title')}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-[480px] max-h-[85dvh] rounded-t-2xl bg-paper flex flex-col overflow-hidden"
        style={{ paddingBottom: 'max(8px, env(safe-area-inset-bottom))' }}
      >
        <div className="shrink-0 px-4 py-3 border-b-2 border-hairline">
          <p className="font-sans text-lg font-bold">
            {selectedPrinter?.name ?? t('waiter.ticket.commandes.reprintKitchen.title')}
          </p>
          <p className="font-mono text-[14px] text-ink-mute">
            {t(`waiter.ticket.commandes.reprintKitchen.${selectedPrinter ? 'roundBody' : 'body'}`)}
          </p>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto waiter-scroll p-3 flex flex-col gap-2">
          {selectedPrinter ? (
            roundsLoading ? (
              <p role="status">{t('loading.label')}</p>
            ) : roundsError ? (
              <>
                <p role="alert">{t('waiter.ticket.errors.generic')}</p>
                <button
                  type="button"
                  className="min-h-[48px] underline"
                  onClick={() => void refetchRounds()}
                >
                  {t('waiter.ticket.commandes.reprintKitchen.retry')}
                </button>
              </>
            ) : rounds && rounds.length > 0 ? (
              <>
                <button
                  type="button"
                  data-testid="kitchen-reprint-all-rounds"
                  disabled={busyPrinterUuid !== null}
                  onClick={() => void handlePrint(selectedPrinter)}
                  className="min-h-[48px] px-3 py-2 border-2 border-ink rounded-btn font-sans text-[18px] font-bold text-left disabled:opacity-40"
                >
                  {t('waiter.ticket.commandes.reprintKitchen.allRounds')}
                </button>
                {rounds.map((round) => (
                  <button
                    key={round}
                    type="button"
                    data-testid={`kitchen-reprint-round-${round}`}
                    disabled={busyPrinterUuid !== null}
                    onClick={() => void handlePrint(selectedPrinter, round)}
                    className="min-h-[48px] px-3 py-2 border-2 border-ink rounded-btn font-sans text-[18px] font-bold text-left disabled:opacity-40"
                  >
                    {t('waiter.ticket.commandes.reprintKitchen.round', { round })}
                  </button>
                ))}
                {busyPrinterUuid && (
                  <p role="status">
                    {t('waiter.ticket.commandes.reprintKitchen.sending', {
                      name: selectedPrinter.name,
                    })}
                  </p>
                )}
              </>
            ) : (
              <p>{t('waiter.ticket.commandes.reprintKitchen.empty')}</p>
            )
          ) : printers && printers.length > 0 ? (
            printers.map((printer) => (
              <button
                key={printer.uuid}
                type="button"
                data-testid={`kitchen-reprint-${printer.uuid}`}
                disabled={busyPrinterUuid !== null}
                onClick={() => (orderUuid ? void handlePrint(printer) : setSelectedPrinter(printer))}
                className="min-h-[48px] px-3 py-2 border-2 border-ink rounded-btn font-sans text-[18px] font-bold text-left disabled:opacity-40"
              >
                {busyPrinterUuid === printer.uuid
                  ? t('waiter.ticket.commandes.reprintKitchen.sending', { name: printer.name })
                  : printer.name}
              </button>
            ))
          ) : isFetching ? (
            <p data-testid="kitchen-reprint-loading" className="font-mono text-[15px]">
              {t('loading.label')}
            </p>
          ) : (
            // Une liste vide se DIT : ouvrir une feuille vide laisse croire à
            // un écran qui n'a pas fini de charger, et le serveur attend.
            <p data-testid="kitchen-reprint-empty" className="font-mono text-[15px]">
              {t('waiter.ticket.commandes.reprintKitchen.none')}
            </p>
          )}
        </div>

        {selectedPrinter && (
          <button
            type="button"
            data-testid="kitchen-reprint-back"
            disabled={busyPrinterUuid !== null}
            onClick={() => setSelectedPrinter(null)}
            className="shrink-0 mx-3 h-12 rounded-btn font-sans text-[18px] font-bold disabled:opacity-40"
          >
            {t('waiter.ticket.commandes.reprintKitchen.changePrinter')}
          </button>
        )}
        <button
          type="button"
          data-testid="kitchen-reprint-cancel"
          onClick={onClose}
          className="shrink-0 mx-3 mb-2 h-12 rounded-btn font-sans text-[18px] font-bold underline"
        >
          {t('common.cancel')}
        </button>
      </div>
    </div>
  );
}
